'use client'

import {useEscapeKey, useFocusTrap, useTranslate} from '@local/hooks'
import {RadioButton, Select, SelectOptionType} from '@local/ui/components'
import {cx} from '@local/utils/cx'

import DateSVG from '@local/assets/svgs/icon-date.svg'
import PersonSVG from '@local/assets/svgs/icon-person.svg'

export interface ProductHeroSelectModalProps {
	isOpen: boolean
	guestOptions: SelectOptionType[]
	roomOptions: SelectOptionType[]
	dateOptions: SelectOptionType[]
	sellingPlanOptions: SelectOptionType[]
	guestAndRoom:{
		guests: {value: string, label: string},
		room: {value: string, label: string},
		date: {value: string, label: string},
	}
	onClickClose: () => void
	onChangeRoom: (e) => void
	onChangeDate: (e) => void
	onChangeGuests: (e) => void
	onChangeSellingPlan: (e) => void
}

export const ProductHeroSelectModal = ({
	isOpen,
	guestOptions,
	roomOptions,
	dateOptions,
	guestAndRoom,
	sellingPlanOptions,
	onClickClose,
	onChangeRoom,
	onChangeDate,
	onChangeGuests,
	onChangeSellingPlan,
}: ProductHeroSelectModalProps) => {
	const trapRef = useFocusTrap({
		includeContainer: true,
		initialFocus: 'container',
		returnFocus: true,
		updateNodes: true,
		isActive: isOpen,
		onClickClose,
	})
	const locale = useTranslate()

	useEscapeKey(onClickClose)

	return (
		<>
			<div
				ref={trapRef}
				className={cx(
					'lg:hidden',
					'fixed bottom-66 margin-left-1 margin-right-1 z-10',
					'flex flex-col gap-12 bg-white rounded-xs p-10',
					'shadow-[0px_0px_3.6px_rgba(0,0,0,0.024),0px_0px_10px_rgba(0,0,0,0.035),0px_0px_24.1px_rgba(0,0,0,0.046),0px_0px_80px_rgba(0,0,0,0.07)]',
					'transform-gpu transition-[transform,opacity,visibility] origin-bottom duration-300 ease-expo-out',
					isOpen
						? 'scale-100 opacity-100 visible'
						: 'scale-90 opacity-0 invisible',
				)}>
				{roomOptions.map((variant) => (
					<RadioButton
						name='mobileRoom'
						key={variant.value}
						value={variant.value}
						id={`product-hero-${variant.value}`}
						checked={guestAndRoom.room.value === variant.value}
						onChange={onChangeRoom}
						theme='light'
						className='w-full items-center text-11 text-normal text-black tracking-wide uppercase leading-none'>
						{variant.label}
					</RadioButton>
				))}

				<Select
					theme='light'
					name='mobileGuest'
					id='product-hero-mobile-guest-select'
					onChange={onChangeGuests}
					options={guestOptions}
					value={guestAndRoom.guests.value}
					prefixIcon={<PersonSVG className='w-16 h-16 mr-4' alt='Person Icon' />}
					titleClassName='!text-9 font-medium'
					containerClassName='w-full mt-2' />
				{dateOptions.length > 1 && (
					<Select
						theme='light'
						name='mobileDate'
						id='product-hero-mobile-date-select'
						onChange={onChangeDate}
						options={dateOptions}
						value={guestAndRoom.date.value}
						prefixIcon={<DateSVG className='w-16 h-16 mr-4' alt='Date Icon' aria-hidden />}
						titleClassName='!text-9 font-medium'
						containerClassName='w-full mt-2' />
				)}
				{!!sellingPlanOptions.length && (
					<Select
						theme='light'
						name='sellingPlan'
						id='product-hero-mobile-selling-plan'
						placeholder={locale('Deffered Payment')}
						onChange={onChangeSellingPlan}
						options={sellingPlanOptions}
						value={guestAndRoom.sellingPlan.value}
						containerClassName='w-full mt-2'
						titleClassName='!text-9 font-medium uppercase' />
				)}
			</div>
			<div
				onClick={onClickClose}
				className={cx(
					'lg:hidden',
					'absolute z-0 inset-0 bg-black/30',
					'transition-[opacity,visibility] duration-500',
					isOpen ? 'opacity-100 visible ease-quart-out' : 'opacity-0 invisible ease-quart-inout',
				)} />
		</>
	)
}
